import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type ColumnFiltersState,
    type SortingState,
} from '@tanstack/react-table';
import { Button, EmptyState, ErrorState } from '@fiado/react-kit';
import { resolveErrorMessage } from '../../../api/errorMapping';
import { Check, Copy } from 'lucide-react';
import { platformApi } from '../../../api/endpoints/platform';
import { Gate } from '../../../components/permissions/Gate';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { USER_STATUS_META, USER_STATUS_OPTIONS, type UserStatus } from '../../../lib/userStatus';
import { Banner } from '../../../components/ui/Banner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { DataTableToolbar } from '../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../components/shared/DataTablePagination';
import { SortableHeader } from '../../../components/shared/SortableHeader';
import { ContentLoading } from '../../../components/shared/ContentLoading';
import { globalFilterFn } from '../../../lib/dataTable';
import { SELECT_CLASS } from '../../../lib/selectClass';
import { PlatformAdminForm } from './PlatformAdminForm';
import { PlatformAdminRowActions } from './PlatformAdminRowActions';
import type {
    PlatformUserListItemLocal,
    CreatePlatformUserResponseLocal,
} from '../../../api/types/PlatformLocal';

const baseColumns: ColumnDef<PlatformUserListItemLocal>[] = [
    {
        accessorKey: 'displayName',
        header: ({ column }) => <SortableHeader column={column} title="Nombre" />,
    },
    {
        accessorKey: 'email',
        header: ({ column }) => <SortableHeader column={column} title="Email" />,
    },
    {
        accessorKey: 'status',
        header: ({ column }) => <SortableHeader column={column} title="Estado" />,
        filterFn: 'equals',
        cell: (info) => <StatusBadge status={info.getValue() as UserStatus | undefined} />,
    },
];

/**
 * Usuarios a nivel PLATAFORMA (`/platform/users`). Misma composición que las otras tablas del
 * módulo (useReactTable + DataTableToolbar/Pagination + SortableHeader + kebab por fila), gateada con los
 * permisos `platform.user.*` (scope PLATFORM). El alta NO lleva rol (la asignación de rol es un flujo
 * aparte → AssignRoleDialog desde el kebab); si el alta devuelve una contraseña temporal, se muestra en
 * un modal one-time. La asignación de rol con scope global/por-tenant vive en AssignRoleDialog.
 */
export function PlatformAdminsTable() {
    const [createOpen, setCreateOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'displayName', desc: false }]);
    const [created, setCreated] = useState<CreatePlatformUserResponseLocal | null>(null);

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['platform', 'admins'],
        queryFn: () => platformApi.listPlatformAdmins(),
    });

    const columns = useMemo<ColumnDef<PlatformUserListItemLocal>[]>(
        () => [
            ...baseColumns,
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                cell: ({ row }) => <PlatformAdminRowActions admin={row.original} />,
            },
        ],
        [],
    );

    const columnFilters: ColumnFiltersState = useMemo(
        () => (statusFilter ? [{ id: 'status', value: statusFilter }] : []),
        [statusFilter],
    );

    const rows = useMemo(() => data?.items ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, columnFilters, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    if (isLoading) return <ContentLoading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar los administradores"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="platform.user.create" scope="PLATFORM">
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear usuario
                    </Button>
                </Gate>
            </div>

            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Crear usuario</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformAdminForm
                            onCreated={(res) => {
                                // Solo abrimos el modal de contraseña si el backend la devolvió.
                                if (res.temporaryPassword) setCreated(res);
                            }}
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <DataTableToolbar value={globalFilter} onChange={setGlobalFilter}>
                <select
                    aria-label="Estado"
                    className={`${SELECT_CLASS} w-auto`}
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                    }}
                >
                    <option value="">Todos</option>
                    {USER_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                            {USER_STATUS_META[s].label}
                        </option>
                    ))}
                </select>
            </DataTableToolbar>

            {rows.length === 0 ? (
                <EmptyState
                    title="Todavía no hay administradores de plataforma"
                    description="Cuando des de alta un administrador, aparecerá aquí."
                />
            ) : (
                <>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map((h) => (
                                        <TableHead key={h.id} className="text-center">
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((r) => (
                                <TableRow key={r.id}>
                                    {r.getVisibleCells().map((c) => (
                                        <TableCell key={c.id} className="text-center align-middle">
                                            {flexRender(c.column.columnDef.cell, c.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <DataTablePagination table={table} />
                </>
            )}

            {/* Contraseña temporal del admin recién creado (one-time). Inline (mismo overlay que el resto):
                el TempPasswordModal de tenants trae copy específico de tenant, así que aquí usamos uno propio. */}
            {created?.temporaryPassword && (
                <AdminTempPasswordModal
                    email={created.email}
                    temporaryPassword={created.temporaryPassword}
                    onClose={() => {
                        setCreated(null);
                    }}
                />
            )}
        </div>
    );
}

function AdminTempPasswordModal({
    email,
    temporaryPassword,
    onClose,
}: {
    email: string;
    temporaryPassword: string;
    onClose: () => void;
}) {
    const [copied, setCopied] = useState(false);
    const handleCopy = (): void => {
        void navigator.clipboard.writeText(temporaryPassword).then(() => {
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        });
    };

    return (
        <div
            role="dialog"
            aria-label="Usuario creado"
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16"
        >
            <button
                type="button"
                aria-label="Cerrar"
                className="fixed inset-0 bg-black/50"
                onClick={onClose}
            />
            <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Usuario creado</h2>
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={onClose}
                    >
                        ✕
                    </button>
                </div>
                <Banner variant="warning" className="mb-4">
                    Guarda esta contraseña ahora: no se vuelve a mostrar.
                </Banner>
                <div className="space-y-3">
                    <div className="space-y-1">
                        <p className="text-sm font-medium">Email</p>
                        <p className="text-sm text-muted-foreground">{email}</p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-sm font-medium">Contraseña temporal</p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-sm">
                                {temporaryPassword}
                            </code>
                            <Button
                                variant="outline"
                                size="sm"
                                aria-label="Copiar contraseña"
                                onClick={handleCopy}
                            >
                                {copied ? (
                                    <Check className="size-4" />
                                ) : (
                                    <Copy className="size-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="mt-6 flex justify-end">
                    <Button onClick={onClose}>Listo</Button>
                </div>
            </div>
        </div>
    );
}
